import React from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { defineMessages, useIntl } from 'react-intl';
import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { AlertTriangle } from '@lifeomic/chromicons';
import { client } from '../../../../util/api';
import {
  deriveJourneyReport,
  describeTouchpoint,
  MEMBER_JOURNEY_QUERY_KEY,
  memberJourneyPath,
  MemberJourneyProjection,
  MemberJourneyStageId,
  reviewReasonFor,
  SignalTone,
  stageLabel,
  toneFor,
  defaultTouchpointFor,
  touchpointLabel,
  TouchpointStatus,
} from './journeyReportModel';

const messages = defineMessages({
  underConstruction: {
    id: 'JourneyReport.underConstruction',
    defaultMessage:
      'UNDER CONSTRUCTION. This page is in early development. Expect inaccuracies, bugs, and lack of polish.',
  },
  noSnapshot: {
    id: 'JourneyReport.noSnapshot',
    defaultMessage: 'No snapshot available',
  },
  unavailable: {
    id: 'JourneyReport.unavailable',
    defaultMessage: 'Unable to load the journey report for this member.',
  },
  kicker: {
    id: 'JourneyReport.kicker',
    defaultMessage: 'Member projection',
  },
  careJourneyYear: {
    id: 'JourneyReport.careJourneyYear',
    defaultMessage: '{year} care journey',
  },
  careJourney: {
    id: 'JourneyReport.careJourney',
    defaultMessage: 'Care journey',
  },
  generated: {
    id: 'JourneyReport.generated',
    defaultMessage: 'Generated {date}',
  },
  reviewSignals: {
    id: 'JourneyReport.reviewSignals',
    defaultMessage: 'Review signals',
  },
  weakEvidence: {
    id: 'JourneyReport.weakEvidence',
    defaultMessage: 'Weak evidence',
  },
  scheduled: {
    id: 'JourneyReport.scheduled',
    defaultMessage: 'Scheduled',
  },
  journeyStages: {
    id: 'JourneyReport.journeyStages',
    defaultMessage: 'Journey stages',
  },
  legendConfirmed: {
    id: 'JourneyReport.legendConfirmed',
    defaultMessage: 'Confirmed',
  },
  legendMixed: {
    id: 'JourneyReport.legendMixed',
    defaultMessage: 'Mixed',
  },
  legendMissing: {
    id: 'JourneyReport.legendMissing',
    defaultMessage: 'Missing',
  },
  stageReviewSignals: {
    id: 'JourneyReport.stageReviewSignals',
    defaultMessage:
      '{count, plural, one {# review signal} other {# review signals}}',
  },
  stageScheduled: {
    id: 'JourneyReport.stageScheduled',
    defaultMessage: '{count, plural, one {# scheduled} other {# scheduled}}',
  },
  stageOnTrack: {
    id: 'JourneyReport.stageOnTrack',
    defaultMessage: 'On track',
  },
  stageTouchpoints: {
    id: 'JourneyReport.stageTouchpoints',
    defaultMessage: '{stage} touchpoints',
  },
  anchor: {
    id: 'JourneyReport.anchor',
    defaultMessage: 'Day of diagnostics anchor · {date}',
  },
  noAnchor: {
    id: 'JourneyReport.noAnchor',
    defaultMessage: 'No day of diagnostics anchor',
  },
  selectedTouchpoint: {
    id: 'JourneyReport.selectedTouchpoint',
    defaultMessage: 'Selected touchpoint',
  },
  footerNote: {
    id: 'JourneyReport.footerNote',
    defaultMessage:
      'Select any stage or touchpoint to inspect its source evidence.',
  },
  confidence: {
    id: 'JourneyReport.confidence',
    defaultMessage: 'Confidence',
  },
  signal: {
    id: 'JourneyReport.signal',
    defaultMessage: 'Signal',
  },
  expected: {
    id: 'JourneyReport.expected',
    defaultMessage: 'Expected',
  },
  expectedDate: {
    id: 'JourneyReport.expectedDate',
    defaultMessage: 'Expected {date}',
  },
  occurred: {
    id: 'JourneyReport.occurred',
    defaultMessage: 'Occurred',
  },
  scheduledFor: {
    id: 'JourneyReport.scheduledFor',
    defaultMessage: 'Scheduled {date}',
  },
  source: {
    id: 'JourneyReport.source',
    defaultMessage: 'Source',
  },
  noEvidence: {
    id: 'JourneyReport.noEvidence',
    defaultMessage: 'No source evidence recorded.',
  },
  badgeOverdue: {
    id: 'JourneyReport.badgeOverdue',
    defaultMessage: 'Overdue · {days, plural, one {# day} other {# days}}',
  },
  badgeInferred: {
    id: 'JourneyReport.badgeInferred',
    defaultMessage: 'Inferred',
  },
  badgeExpected: {
    id: 'JourneyReport.badgeExpected',
    defaultMessage: 'Not yet due',
  },
  stageFallback: {
    id: 'JourneyReport.stageFallback',
    defaultMessage: 'Stage fallback',
  },
  noDate: {
    id: 'JourneyReport.noDate',
    defaultMessage: 'No date',
  },
  whyReview: {
    id: 'JourneyReport.whyReview',
    defaultMessage: 'Why this needs review:',
  },
  calloutTaskOpen: {
    id: 'JourneyReport.calloutTaskOpen',
    defaultMessage:
      'the matching Salesforce task exists but remains "{status}" after the expected date.',
  },
  calloutTaskOpenNoStatus: {
    id: 'JourneyReport.calloutTaskOpenNoStatus',
    defaultMessage:
      'the matching Salesforce task exists but has not been completed.',
  },
  calloutNoEvidence: {
    id: 'JourneyReport.calloutNoEvidence',
    defaultMessage:
      'no appointment, task, or form evidence was found for this touchpoint.',
  },
  items: {
    id: 'JourneyReport.items',
    defaultMessage: 'Items',
  },
});

const COLORS = {
  ink: '#132839',
  muted: '#6b7c87',
  line: '#cad7dc',
  border: '#d2dde1',
  paper: '#f3f6f7',
  teal: '#147c74',
  tealSoft: '#b8ddd7',
  amber: '#d58920',
  amberSoft: '#fff0d4',
  amberInk: '#8a560d',
  red: '#c64b45',
  redSoft: '#fbe8e6',
  redInk: '#a23b36',
};

const useStyles = makeStyles(theme => ({
  pageContent: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    padding: theme.spacing(2),
    color: COLORS.ink,
    fontSize: 13,
  },
  summary: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  kicker: {
    color: COLORS.muted,
    fontSize: 9,
    fontWeight: 850,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
  },
  memberTitle: {
    margin: '4px 0 3px',
    fontSize: 24,
    lineHeight: 1.1,
    fontWeight: 800,
  },
  meta: { color: COLORS.muted, fontSize: 11 },
  chip: {
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    padding: '2px 8px',
    border: `1px solid ${COLORS.border}`,
    borderRadius: 99,
    fontSize: 10,
  },
  stats: { display: 'flex', gap: theme.spacing(1) },
  stat: {
    minWidth: 84,
    padding: '9px 11px',
    border: `1px solid ${COLORS.border}`,
    borderRadius: 11,
    background: '#fff',
    '& b': { display: 'block', fontSize: 17 },
    '& span': { color: COLORS.muted, fontSize: 9 },
  },
  statAlert: { '& b': { color: COLORS.red } },
  statWeak: { '& b': { color: COLORS.amber } },
  card: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 16,
    background: '#fff',
    marginTop: theme.spacing(1.75),
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderBottom: '1px solid #e3eaed',
    '& strong': { fontSize: 12 },
  },
  legend: {
    display: 'flex',
    gap: 13,
    color: COLORS.muted,
    fontSize: 9,
    '& span': { display: 'flex', alignItems: 'center', gap: 4 },
    '& i': {
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: COLORS.teal,
    },
  },
  legendAmber: { background: `${COLORS.amber} !important` },
  legendRed: { background: `${COLORS.red} !important` },
  legendScheduled: { background: `${COLORS.tealSoft} !important` },
  railWrap: { overflowX: 'auto', padding: '32px 24px 26px' },
  stageRail: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(125px, 1fr))',
    minWidth: 790,
  },
  stageNode: {
    position: 'relative',
    textAlign: 'center',
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    '&:before': {
      content: '""',
      position: 'absolute',
      zIndex: 0,
      top: 19,
      left: '-50%',
      width: '100%',
      height: 3,
      background: COLORS.line,
    },
    '&:first-child:before': { display: 'none' },
    '&:focus-visible $stageDot': {
      outline: `3px solid ${theme.palette.primary.main}`,
    },
  },
  stageDot: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    placeItems: 'center',
    width: 42,
    height: 42,
    margin: '0 auto 11px',
    border: `3px solid ${COLORS.line}`,
    borderRadius: '50%',
    background: '#fff',
    color: COLORS.muted,
    fontSize: 11,
    fontWeight: 850,
  },
  stageDotConfirmed: {
    borderColor: COLORS.teal,
    background: COLORS.teal,
    color: '#fff',
  },
  stageDotMixed: {
    borderColor: COLORS.amber,
    background: COLORS.amberSoft,
    color: COLORS.amberInk,
  },
  stageDotSelected: { outline: '5px solid #dce9ee' },
  stageName: { display: 'block', fontSize: 10, fontWeight: 780 },
  stageCount: {
    display: 'block',
    marginTop: 3,
    color: COLORS.muted,
    fontSize: 9,
  },
  stageCountAlert: { color: COLORS.red, fontWeight: 750 },
  detailTitle: { display: 'flex', alignItems: 'center', gap: 9 },
  stageIndex: {
    display: 'grid',
    placeItems: 'center',
    width: 23,
    height: 23,
    borderRadius: 7,
    background: COLORS.amberSoft,
    color: COLORS.amberInk,
    fontSize: 10,
    fontWeight: 850,
  },
  touchRail: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(110px, 1fr)',
    padding: '34px 22px 30px',
    minWidth: 'max-content',
  },
  touch: {
    position: 'relative',
    textAlign: 'center',
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    minWidth: 110,
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 13,
      left: '-50%',
      width: '100%',
      height: 2,
      background: '#dbe4e7',
    },
    '&:first-child:before': { display: 'none' },
    '&:focus-visible $touchDot': {
      outline: `3px solid ${theme.palette.primary.main}`,
    },
  },
  touchDot: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    placeItems: 'center',
    width: 30,
    height: 30,
    margin: '0 auto 10px',
    border: `2px solid ${COLORS.line}`,
    borderRadius: '50%',
    background: '#fff',
    color: COLORS.muted,
    fontSize: 10,
    fontWeight: 850,
  },
  touchDotConfirmed: {
    borderColor: COLORS.teal,
    background: COLORS.teal,
    color: '#fff',
  },
  touchDotWeak: {
    borderColor: COLORS.amber,
    background: COLORS.amberSoft,
    color: COLORS.amberInk,
  },
  touchDotScheduled: {
    borderColor: COLORS.tealSoft,
    background: '#fff',
    color: COLORS.teal,
  },
  touchDotMissing: {
    borderColor: COLORS.red,
    background: COLORS.redSoft,
    color: COLORS.redInk,
  },
  touchDotSelected: { outline: '4px solid #f5d9d6' },
  touchName: {
    display: 'block',
    minHeight: 30,
    fontSize: 9,
    fontWeight: 760,
    lineHeight: 1.25,
    padding: '0 4px',
  },
  touchDate: {
    display: 'block',
    marginTop: 5,
    color: COLORS.muted,
    fontSize: 8,
  },
  touchDateMissing: { color: COLORS.red, fontWeight: 750 },
  below: { marginTop: theme.spacing(1.75) },
  inspector: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 16,
    background: '#fff',
    padding: theme.spacing(2),
  },
  inspectHead: { display: 'flex', justifyContent: 'space-between', gap: 15 },
  inspectTitle: { margin: '3px 0 2px', fontSize: 16, fontWeight: 700 },
  badge: {
    height: 'max-content',
    padding: '5px 8px',
    borderRadius: 99,
    background: COLORS.paper,
    color: COLORS.muted,
    fontSize: 8,
    fontWeight: 850,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  badgeMissing: { background: COLORS.redSoft, color: '#9f3934' },
  badgeWeak: { background: COLORS.amberSoft, color: COLORS.amberInk },
  badgeConfirmed: { background: '#e1f1ee', color: COLORS.teal },
  evidence: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginTop: 14,
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
  },
  datum: {
    padding: 10,
    borderRadius: 9,
    background: COLORS.paper,
    minWidth: 0,
    '& span': {
      display: 'block',
      color: COLORS.muted,
      fontSize: 8,
      textTransform: 'uppercase',
      letterSpacing: '.08em',
    },
    '& b': {
      display: 'block',
      marginTop: 3,
      font: '10px ui-monospace, SFMono-Regular, monospace',
      overflowWrap: 'anywhere',
    },
  },
  callout: {
    marginTop: 11,
    padding: '10px 12px',
    borderLeft: `3px solid ${COLORS.red}`,
    borderRadius: '3px 8px 8px 3px',
    background: COLORS.redSoft,
    fontSize: 10,
  },
  sideList: { marginTop: 10 },
  sideRow: {
    display: 'grid',
    gridTemplateColumns: '8px 1fr auto',
    gap: 7,
    padding: '8px 0',
    borderTop: '1px solid #e5ebed',
    fontSize: 9,
    width: '100%',
    background: 'none',
    border: 0,
    borderTopStyle: 'solid',
    textAlign: 'left',
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    alignItems: 'start',
    '& i': {
      width: 7,
      height: 7,
      marginTop: 3,
      borderRadius: '50%',
    },
    '& em': { color: COLORS.muted, fontStyle: 'normal' },
  },
  footerNote: { marginTop: 13, color: COLORS.muted, fontSize: 10 },
}));

type Classes = ReturnType<typeof useStyles>;

const DOT_GLYPH: Record<TouchpointStatus['kind'], string> = {
  confirmed: '✓',
  inferred: '~',
  scheduled: '○',
  overdue: '!',
  missing: '!',
  expected: '○',
};

const TONE_DOT_COLORS: Record<SignalTone, string> = {
  confirmed: COLORS.teal,
  weak: COLORS.amber,
  scheduled: COLORS.tealSoft,
  missing: COLORS.red,
  pending: COLORS.line,
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const truncateId = (id: string) =>
  id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;

const isNotFound = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 404;

type JourneyReportContentProps = {
  member: fhir.Patient | null;
  memberId: string;
  fallback: React.ReactNode;
};

export const JourneyReportContent: React.FC<JourneyReportContentProps> = ({
  member,
  memberId,
  fallback,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const journeyQuery = useQuery({
    queryKey: [...MEMBER_JOURNEY_QUERY_KEY, memberId],
    queryFn: async (): Promise<MemberJourneyProjection> => {
      const response = await client.get<MemberJourneyProjection>(
        memberJourneyPath(memberId),
      );
      return response.data;
    },
    enabled: Boolean(memberId),
    retry: false,
    refetchOnWindowFocus: false,
  });

  if (!member) {
    return <>{fallback}</>;
  }

  const banner = (
    <Alert statusType="warning" fullWidth data-testid="journey-report-banner">
      <AlertIcon icon={AlertTriangle} />
      <AlertBody>
        <Text size="subbody">{formatMessage(messages.underConstruction)}</Text>
      </AlertBody>
    </Alert>
  );

  if (journeyQuery.isLoading) {
    return (
      <>
        {banner}
        <Box fullWidth fullHeight align="center" justify="center" gap={1}>
          <DotLoader />
        </Box>
      </>
    );
  }

  if (journeyQuery.isError || !journeyQuery.data) {
    return (
      <>
        {banner}
        <Box fullWidth fullHeight align="center" justify="center">
          <Text size="subbody">
            {formatMessage(
              journeyQuery.isError && !isNotFound(journeyQuery.error)
                ? messages.unavailable
                : messages.noSnapshot,
            )}
          </Text>
        </Box>
      </>
    );
  }

  return (
    <>
      {banner}
      <div className={classes.pageContent}>
        <JourneyReportView
          key={journeyQuery.data.generatedAt}
          projection={journeyQuery.data}
          classes={classes}
        />
      </div>
    </>
  );
};

const JourneyReportView: React.FC<{
  projection: MemberJourneyProjection;
  classes: Classes;
}> = ({ projection, classes }) => {
  const { formatMessage, formatDate } = useIntl();
  // One clock reading per mount (parent remounts on each new projection) so
  // every tone, count, and badge agrees.
  const [now] = React.useState(() => new Date());
  const report = React.useMemo(
    () => deriveJourneyReport(projection, now),
    [projection, now],
  );
  const [selectedStageId, setSelectedStageId] =
    React.useState<MemberJourneyStageId | null>(report.defaultStageId);
  const [selectedTouchpointId, setSelectedTouchpointId] = React.useState<
    string | null
  >(report.defaultTouchpointId);

  const selectedStage =
    projection.stages.find(s => s.id === selectedStageId) ??
    projection.stages[0];
  const selectedStageIndex = projection.stages.indexOf(selectedStage);
  const selectedTouchpoint =
    selectedStage?.touchpoints.find(tp => tp.id === selectedTouchpointId) ??
    selectedStage?.touchpoints[0];

  const selectStage = (stageId: MemberJourneyStageId) => {
    const stage = projection.stages.find(s => s.id === stageId);
    setSelectedStageId(stageId);
    setSelectedTouchpointId(
      defaultTouchpointFor(stage?.touchpoints ?? [], now)?.id ?? null,
    );
  };

  // expectedAt and the diagnostics anchor are UTC calendar days (YYYY-MM-DD);
  // formatting them in the viewer's zone shifts them a day west of UTC.
  const zoneFor = (iso: string) =>
    DATE_ONLY.test(iso) ? { timeZone: 'UTC' } : {};
  const shortDate = (iso?: string) =>
    iso
      ? formatDate(iso, { month: 'short', day: 'numeric', ...zoneFor(iso) })
      : formatMessage(messages.noDate);
  const mediumDate = (iso?: string) =>
    iso ? formatDate(iso, { dateStyle: 'medium', ...zoneFor(iso) }) : '—';

  const statusLine = (status: TouchpointStatus): string => {
    switch (status.kind) {
      case 'confirmed':
        return shortDate(status.occurredAt);
      case 'inferred':
        return status.viaStage
          ? formatMessage(messages.stageFallback)
          : shortDate(status.occurredAt);
      case 'scheduled':
        return formatMessage(messages.scheduledFor, {
          date: shortDate(status.scheduledAt),
        });
      case 'overdue':
      case 'expected':
        return formatMessage(messages.expectedDate, {
          date: shortDate(status.expectedAt),
        });
      case 'missing':
        return formatMessage(messages.noDate);
    }
  };

  const badgeFor = (status: TouchpointStatus): string => {
    switch (status.kind) {
      case 'confirmed':
        return formatMessage(messages.legendConfirmed);
      case 'inferred':
        return formatMessage(messages.badgeInferred);
      case 'scheduled':
        return formatMessage(messages.scheduled);
      case 'overdue':
        return formatMessage(messages.badgeOverdue, {
          days: status.daysOverdue,
        });
      case 'missing':
        return formatMessage(messages.legendMissing);
      case 'expected':
        return formatMessage(messages.badgeExpected);
    }
  };

  const toneDotClass: Record<SignalTone, string> = {
    confirmed: classes.touchDotConfirmed,
    weak: classes.touchDotWeak,
    scheduled: classes.touchDotScheduled,
    missing: classes.touchDotMissing,
    pending: '',
  };
  const toneBadgeClass: Record<SignalTone, string> = {
    confirmed: classes.badgeConfirmed,
    weak: classes.badgeWeak,
    scheduled: classes.badgeConfirmed,
    missing: classes.badgeMissing,
    pending: '',
  };

  const selectedStageSummary = report.stages[selectedStageIndex];
  const reviewReason = selectedTouchpoint
    ? reviewReasonFor(selectedTouchpoint, now)
    : null;
  const selectedStatus = selectedTouchpoint
    ? describeTouchpoint(selectedTouchpoint, now)
    : null;
  const { membership, salesforce } = projection;

  return (
    <>
      <div className={classes.summary}>
        <div>
          <div className={classes.kicker}>{formatMessage(messages.kicker)}</div>
          <div className={classes.memberTitle}>
            {membership.packageYear
              ? formatMessage(messages.careJourneyYear, {
                  year: membership.packageYear,
                })
              : formatMessage(messages.careJourney)}
          </div>
          <div className={classes.meta}>
            {[
              truncateId(projection.patientId),
              membership.status,
              membership.termStart || membership.termEnd
                ? `${mediumDate(membership.termStart)} – ${mediumDate(
                    membership.termEnd,
                  )}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
          <div className={classes.meta}>
            {formatMessage(messages.generated, {
              date: formatDate(projection.generatedAt, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }),
            })}
            {salesforce.stage ? (
              <span className={classes.chip}>{salesforce.stage}</span>
            ) : null}
          </div>
        </div>
        <div className={classes.stats}>
          <div className={clsx(classes.stat, classes.statAlert)}>
            <b>{report.totals.reviewSignals}</b>
            <span>{formatMessage(messages.reviewSignals)}</span>
          </div>
          <div className={clsx(classes.stat, classes.statWeak)}>
            <b>{report.totals.weak}</b>
            <span>{formatMessage(messages.weakEvidence)}</span>
          </div>
          <div className={classes.stat}>
            <b>{report.totals.scheduled}</b>
            <span>{formatMessage(messages.scheduled)}</span>
          </div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardHead}>
          <strong>{formatMessage(messages.journeyStages)}</strong>
          <div className={classes.legend}>
            <span>
              <i />
              {formatMessage(messages.legendConfirmed)}
            </span>
            <span>
              <i className={classes.legendAmber} />
              {formatMessage(messages.legendMixed)}
            </span>
            <span>
              <i className={classes.legendRed} />
              {formatMessage(messages.legendMissing)}
            </span>
            <span>
              <i className={classes.legendScheduled} />
              {formatMessage(messages.scheduled)}
            </span>
          </div>
        </div>
        <div className={classes.railWrap}>
          <div className={classes.stageRail}>
            {report.stages.map(stage => {
              const selected = stage.id === selectedStage?.id;
              return (
                <button
                  key={stage.id}
                  type="button"
                  className={classes.stageNode}
                  aria-pressed={selected}
                  onClick={() => selectStage(stage.id)}
                >
                  <div
                    className={clsx(classes.stageDot, {
                      [classes.stageDotConfirmed]: stage.tone === 'confirmed',
                      [classes.stageDotMixed]: stage.tone === 'mixed',
                      [classes.stageDotSelected]: selected,
                    })}
                  >
                    {stage.complete}/{stage.total}
                  </div>
                  <span className={classes.stageName}>{stage.label}</span>
                  <span
                    className={clsx(classes.stageCount, {
                      [classes.stageCountAlert]: stage.hasAlert,
                    })}
                  >
                    {stage.reviewSignals > 0
                      ? formatMessage(messages.stageReviewSignals, {
                          count: stage.reviewSignals,
                        })
                      : stage.scheduled > 0
                      ? formatMessage(messages.stageScheduled, {
                          count: stage.scheduled,
                        })
                      : formatMessage(messages.stageOnTrack)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {selectedStage ? (
        <div className={classes.card}>
          <div className={classes.cardHead}>
            <div className={classes.detailTitle}>
              <span className={classes.stageIndex}>
                {String(selectedStageIndex + 1).padStart(2, '0')}
              </span>
              <strong>
                {formatMessage(messages.stageTouchpoints, {
                  stage:
                    selectedStageSummary?.label ?? stageLabel(selectedStage.id),
                })}
              </strong>
            </div>
            <span className={classes.meta}>
              {projection.anchors.dayOfDiagnostics
                ? formatMessage(messages.anchor, {
                    date: mediumDate(projection.anchors.dayOfDiagnostics),
                  })
                : formatMessage(messages.noAnchor)}
            </span>
          </div>
          <div className={classes.railWrap} style={{ padding: 0 }}>
            <div className={classes.touchRail}>
              {selectedStage.touchpoints.map(tp => {
                const status = describeTouchpoint(tp, now);
                const tone = toneFor(tp, now);
                const selected = tp.id === selectedTouchpoint?.id;
                return (
                  <button
                    key={tp.id}
                    type="button"
                    className={classes.touch}
                    aria-pressed={selected}
                    onClick={() => setSelectedTouchpointId(tp.id)}
                  >
                    <div
                      className={clsx(classes.touchDot, toneDotClass[tone], {
                        [classes.touchDotSelected]: selected,
                      })}
                    >
                      {DOT_GLYPH[status.kind]}
                    </div>
                    <span className={classes.touchName}>
                      {touchpointLabel(tp.id)}
                    </span>
                    <span
                      className={clsx(classes.touchDate, {
                        [classes.touchDateMissing]: tone === 'missing',
                      })}
                    >
                      {statusLine(status)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      <div className={classes.below}>
        <div className={classes.inspector}>
          {selectedTouchpoint && selectedStatus ? (
            <>
              <div className={classes.inspectHead}>
                <div>
                  <div className={classes.kicker}>
                    {formatMessage(messages.selectedTouchpoint)}
                  </div>
                  <div className={classes.inspectTitle}>
                    {touchpointLabel(selectedTouchpoint.id)}
                  </div>
                  <div className={classes.meta}>{selectedTouchpoint.id}</div>
                </div>
                <span
                  className={clsx(
                    classes.badge,
                    toneBadgeClass[toneFor(selectedTouchpoint, now)],
                  )}
                >
                  {badgeFor(selectedStatus)}
                </span>
              </div>
              <div className={classes.evidence}>
                <div className={classes.datum}>
                  <span>{formatMessage(messages.confidence)}</span>
                  <b>{selectedTouchpoint.confidence}</b>
                </div>
                <div className={classes.datum}>
                  <span>{formatMessage(messages.signal)}</span>
                  <b>{selectedTouchpoint.signal}</b>
                </div>
                <div className={classes.datum}>
                  <span>
                    {formatMessage(
                      selectedTouchpoint.occurredAt
                        ? messages.occurred
                        : selectedTouchpoint.scheduledAt
                        ? messages.scheduled
                        : messages.expected,
                    )}
                  </span>
                  <b>
                    {selectedTouchpoint.occurredAt ??
                      selectedTouchpoint.scheduledAt ??
                      selectedTouchpoint.expectedAt ??
                      '—'}
                  </b>
                </div>
              </div>
              {reviewReason ? (
                <div className={classes.callout}>
                  <strong>{formatMessage(messages.whyReview)}</strong>{' '}
                  {reviewReason.kind === 'task_open'
                    ? reviewReason.status
                      ? formatMessage(messages.calloutTaskOpen, {
                          status: reviewReason.status,
                        })
                      : formatMessage(messages.calloutTaskOpenNoStatus)
                    : formatMessage(messages.calloutNoEvidence)}
                </div>
              ) : null}
              {selectedTouchpoint.evidence.length === 0 ? (
                <div className={classes.footerNote}>
                  {formatMessage(messages.noEvidence)}
                </div>
              ) : (
                selectedTouchpoint.evidence.map((evidence, index) => (
                  <div className={classes.evidence} key={index}>
                    <div className={classes.datum}>
                      <span>{formatMessage(messages.source)}</span>
                      <b>{evidence.kind}</b>
                    </div>
                    {Object.entries(evidence)
                      .filter(([key, value]) => key !== 'kind' && value != null)
                      .map(([key, value]) => (
                        <div className={classes.datum} key={key}>
                          <span>{key}</span>
                          <b>{String(value)}</b>
                        </div>
                      ))}
                  </div>
                ))
              )}
              {selectedTouchpoint.items?.length ? (
                <div className={classes.sideList}>
                  <div className={classes.kicker}>
                    {formatMessage(messages.items)}
                  </div>
                  {selectedTouchpoint.items.map(item => (
                    <div
                      key={item.id}
                      className={classes.sideRow}
                      style={{ cursor: 'default' }}
                    >
                      <i
                        style={{
                          background: TONE_DOT_COLORS[toneFor(item, now)],
                        }}
                      />
                      <span>{touchpointLabel(item.id)}</span>
                      <em>{statusLine(describeTouchpoint(item, now))}</em>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <Text size="subbody">{formatMessage(messages.footerNote)}</Text>
          )}
        </div>
      </div>
    </>
  );
};
